import type { ResolvedDataSource } from '@de/providers/DataSourceManager.types';
import { getByPath, parse, InterpolationError } from '@de/utils/interpolation';
import type { PathSegment } from '@de/utils/interpolation/types';
import type { BoundComponentInfo } from './findBoundComponent';

const warnedReasons: Record<string, true> = {};

/**
 * Resolve the display label for a `formKey` selection.
 *
 * Algorithm:
 *   1. If the bound component declares both `optionLabelField` and
 *      `optionValueField`:
 *        a. Reach into `source.data` via the bind's `optionsPath`
 *           (e.g. `degrees`, `degrees[*].categories`) using the
 *           runtime's interpolation grammar — same path the
 *           binding-resolver used to render the dropdown options,
 *           so display-label resolution looks at the same entries.
 *        b. Deep-flatten the result (mirroring `applyOptions`) so
 *           array-of-arrays shapes from `[*]` paths reduce to a
 *           flat list of choice objects.
 *        c. Find the entry whose `[optionValueField] === value` and
 *           return `entry[optionLabelField]` coerced to a string.
 *   2. Otherwise (or on miss / async-not-yet-resolved source) fall
 *      back to `String(value)` so the selection is never blocked.
 *      Each distinct fallback reason emits one `console.warn` per
 *      process so authors notice missing companion configuration
 *      without drowning the console on repeated commits.
 *
 * IMPORTANT — only `Option.text` is affected. The committed
 * `Option.value` and `formState[formKey]` continue to carry the raw
 * id (e.g. `"2"`). That's what downstream sources interpolate via
 * `{{formState.degreeId}}` for filtered Mobius criteria, so the
 * resolved label MUST NOT leak back into either of those slots.
 *
 * Pure function; no React, no DSM access. Caller hands in the
 * resolved `ResolvedDataSource` snapshot and the bound-component
 * metadata.
 */
export function resolveDisplayLabel(
  value: string,
  bound: BoundComponentInfo | undefined,
  source: ResolvedDataSource | undefined,
): string {
  const fallback = String(value);
  if (!bound?.optionLabelField || !bound.optionValueField) {
    warnOnce(`missing-companion-fields:${bound?.formKey ?? '?'}`);
    return fallback;
  }
  const sourceData = source?.data;
  if (sourceData === undefined || sourceData === null) {
    warnOnce(`source-not-resolved:${bound.source ?? '?'}`);
    return fallback;
  }

  // Reach the option array via the same path the renderer used.
  // Without `optionsPath`, fall back to treating `source.data` as
  // the array directly — this preserves the behaviour for legacy
  // callers/tests that hand in an already-extracted array.
  const items = collectItems(sourceData, bound.optionsPath, bound.source);
  if (items.length === 0) {
    warnOnce(`source-not-resolved:${bound.source ?? '?'}`);
    return fallback;
  }

  const labelField = bound.optionLabelField;
  const valueField = bound.optionValueField;
  for (let i = 0; i < items.length; i++) {
    const entry = items[i];
    if (entry && typeof entry === 'object') {
      const candidate = (entry as Record<string, unknown>)[valueField];
      if (candidate !== undefined && String(candidate) === value) {
        const label = (entry as Record<string, unknown>)[labelField];
        if (label !== undefined && label !== null) {
          return String(label);
        }
      }
    }
  }
  warnOnce(`no-match:${bound.source ?? '?'}:${value}`);
  return fallback;
}

/**
 * Pull the option-list array out of a source payload, applying the
 * same bind-path traversal + deep-flatten the renderer used. Returns
 * `[]` when the path doesn't reach an array (or the path is
 * malformed) so the caller can warn-and-fall-back uniformly.
 *
 * `optionsPath === undefined` is the legacy entrypoint: the caller
 * already handed us the option array directly. We keep that working
 * because (a) older specs in this suite construct fixtures that
 * way, and (b) a future bind shape that elides the path would
 * otherwise lose label resolution.
 */
function collectItems(
  data: unknown,
  optionsPath: string | undefined,
  sourceName: string | undefined,
): readonly unknown[] {
  if (optionsPath === undefined) {
    // Legacy entrypoint: caller already extracted the array.
    return Array.isArray(data) ? flatten(data) : [];
  }
  let segments: ReadonlyArray<PathSegment> | undefined;
  try {
    // `parse('{{<path>}}')` returns a Template whose first segment
    // is the parsed Expression. We pull `segments` off the
    // expression node — the same pre-parsed shape the binding
    // resolver hands to `getByPath` via `cachedParsePath`.
    const parsed = parse(`{{${optionsPath}}}`);
    const expr = parsed.segments[0];
    if (expr && expr.type === 'expression' && expr.kind === 'path') {
      segments = expr.segments;
    }
  } catch (err) {
    if (err instanceof InterpolationError) {
      warnOnce(`bad-options-path:${sourceName ?? '?'}:${optionsPath}`);
      return [];
    }
    throw err;
  }
  if (!segments || segments.length === 0) return [];
  // `data` is the source's response payload — same root the binding
  // resolver hands to `getByPath` (`rootForSource(...)` returns
  // `resolved?.data`). For a Mobius response shaped
  // `{ degrees: [...] }` and path `degrees`, the walker steps into
  // `data['degrees']` and returns the inner array; for the cascading
  // `degrees[*].categories` shape it produces the expected array-of-
  // arrays which `flatten` then unwraps.
  const reached = getByPath(data, segments);
  if (!Array.isArray(reached)) return [];
  return flatten(reached);
}

/**
 * Recursively unwrap nested arrays into a flat list. Mirrors
 * {@link applyOptions}'s own flatten step so the entries we look
 * up by `value` are the same entries the user picked from. The
 * runtime's `[*]` selector deliberately preserves array-of-arrays
 * shape (see `getByPath.ts` §"strict one-level flatten"); display-
 * label resolution undoes it the same way the renderer does.
 */
function flatten(input: ReadonlyArray<unknown>): unknown[] {
  const out: unknown[] = [];
  const visit = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) visit(v[i]);
      return;
    }
    out.push(v);
  };
  visit(input);
  return out;
}

function warnOnce(reason: string): void {
  if (warnedReasons[reason]) return;
  warnedReasons[reason] = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[ConfigurableStep] Display label fell back to the raw value (reason: ${reason}). Check that the bound source has resolved and that \`_bind.optionLabelField\` / \`_bind.optionValueField\` are configured.`,
  );
}

/** Test helper — resets the warn-once latch so spec runs are deterministic. */
export function __resetDisplayLabelWarnLatch(): void {
  for (const key of Object.keys(warnedReasons)) {
    delete warnedReasons[key];
  }
}
